import "highcharts/esm/modules/price-indicator.src.js"
import "highcharts/esm/modules/accessibility.src.js"
import "./chart.css"

import {
  type HighchartsReactRefObject,
  StockChart,
} from "@highcharts/react/Stock"
import { useTheme as useMuiTheme } from "@mui/material"
import type { Options, Series } from "highcharts"
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"

import { useOhlcChartData } from "@/App/Equities/shared/services/marketData"
import { Loader } from "@/components/Loader"
import { ThemeName, useTheme } from "@/theme"
import { withSubscribe } from "@/utils/withSubscribe"

import { currentSymbol$, useCurrentSymbol } from "./state"

const NEUTRAL_BAND_BPS = 1 // dead zone for small price movements within the neutral band, used for tickColor

const HighchartsChart = () => {
  const { t } = useTranslation()
  const symbol = useCurrentSymbol()
  const data = useOhlcChartData()

  const { palette } = useMuiTheme()
  const { themeName } = useTheme()
  const isDark = themeName === ThemeName.Dark

  const chartRef = useRef<HighchartsReactRefObject>(null)

  const tickColor = useMemo(() => {
    const bar = data.at(-1)
    if (!bar) return palette["fg-tertiary"]

    const delta = bar.close - bar.open
    const band = bar.open * (NEUTRAL_BAND_BPS / 10_000)

    if (Math.abs(delta) <= band) {
      return palette["fg-tertiary"]
    }

    return delta > 0 ? palette.success.main : palette.error.main
  }, [data, palette])

  const commonAxisOptions = useMemo(
    () => ({
      gridLineWidth: 1,
      gridLineColor: palette["border-secondary"],
      labels: {
        style: {
          color: palette["text-primary"],
        },
        zIndex: 5,
      },
      lineColor: palette["border-primary"],
    }),
    [palette],
  )

  const options = useMemo(
    () =>
      ({
        accessibility: {
          enabled: true,
          description: t("chart.description", { symbol }),
          typeDescription: t("chart.typeDescription"),
          keyboardNavigation: { enabled: true },
          announceNewData: { enabled: false },
          point: {
            valueDecimals: 2,
            valuePrefix: "$",
          },
          series: {
            describeSingleSeries: true,
          },
        },
        chart: {
          backgroundColor: "transparent",
          animation: false,
        },
        navigator: {
          maskInside: true,
          maskFill: isDark
            ? "rgba(217, 218, 253, 0.2)" // bg-brand-secondary with opacity 0.2
            : "rgba(103, 106, 198, 0.1)", // bg-brand-primary with opacity 0.1
          outlineColor: palette["border-secondary"],
          outlineWidth: 1,
          handles: {
            backgroundColor: palette["bg-tertiary"],
            borderColor: palette["border-primary"],
          },
          series: {
            color: palette["fg-quinary"],
            lineColor: palette["fg-quinary"],
            fillOpacity: 0.15,
          },
          xAxis: {
            gridLineColor: palette["border-secondary"],
            labels: {
              style: {
                color: palette.text.primary,
                fontWeight: "bold",
                textOutline: "none",
              },
            },
          },
        },
        plotOptions: {
          candlestick: {
            color: palette.error.main,
            lineColor: palette.error.main,
            upColor: palette.success.main,
            upLineColor: palette.success.main,
          },
          series: {
            animation: false,
            lastPrice: {
              enabled: true,
              color: tickColor,
              dashStyle: "ShortDash",
              label: {
                enabled: true,
                align: "left",
                format: "{value:.2f}",
                shape: "callout",
                backgroundColor: tickColor,
                borderColor: palette["border-primary"],
                borderWidth: 1,
                style: {
                  color: palette.common.white,
                  fontWeight: "bold",
                },
              },
            },
          },
        },
        rangeSelector: {
          enabled: false,
        },
        series: [
          {
            id: "ohlc",
            type: "candlestick",
            name: symbol,
          },
        ],
        tooltip: {
          valueDecimals: 2,
        },
        xAxis: {
          ...commonAxisOptions,
        },
        yAxis: {
          ...commonAxisOptions,
          crosshair: {
            label: {
              enabled: true,
              backgroundColor: palette["fg-tertiary"],
              format: "{value:.2f}",
            },
          },
          offset: 40,
        },
      }) satisfies Options,
    [commonAxisOptions, isDark, palette, symbol, t, tickColor],
  )

  useEffect(() => {
    const series = chartRef.current?.chart.get("ohlc") as Series | undefined
    series?.setData(data, true, false, true)
  }, [data])

  return (
    <StockChart
      containerProps={{
        className: "h-full equities-chart",
      }}
      options={options}
      ref={chartRef}
    />
  )
}

export const Chart = withSubscribe(HighchartsChart, {
  fallback: <Loader />,
  source$: currentSymbol$,
})
